import React from 'react';
import clsx from 'clsx';
import { BRAND_TEXT, BRAND_WORD, hasBrandWord, splitBrandWord } from 'fym-shared/src/music24/brandWord';
import { TypedMemo } from '../../../common/types';
import styles from './BrandWord.module.scss';

export interface BrandWordProps {
  className?: string;
}

/**
 * The brand in running text, drawn like the logo: "music" in the brand word font, then a small
 * "24" in the brand figure font. The DOM text is "music24", so copy, search and screen readers
 * read the normal word. It takes the colour and the size of the text around it.
 *
 * Use it only where the text is drawn. Attributes (title, alt, aria-label, placeholder), the
 * document title, toasts and values sent to the API keep the plain "MUSIC24".
 */
const BrandWordComponent: React.FC<BrandWordProps> = ({ className }) => (
  <span className={clsx(styles.brand, className)}>
    <span className={styles.word}>{BRAND_WORD.word}</span>
    <span className={styles.num}>{BRAND_WORD.number}</span>
  </span>
);

export const BrandWord = TypedMemo(BrandWordComponent);

const brandParts = (text: string, keyPrefix: string): React.ReactNode => {
  if (!hasBrandWord(text)) {
    return text;
  }
  // Each brand word takes its character offset in the text as its key: stable for one text.
  let offset = 0;
  return splitBrandWord(text).map((part) => {
    const at = offset;
    if (part.kind === 'text') {
      offset += part.text.length;
      return part.text;
    }
    offset += BRAND_TEXT.length;
    return <BrandWord key={`${keyPrefix}brand-${at}`} />;
  });
};

/**
 * Draws each "MUSIC24" of a text as the brand word. A string with no brand word comes back as
 * the same string. A rich-text result (an array of strings and elements, as `formatMessage`
 * returns with element values) keeps its elements and splits only its strings.
 */
export function withBrandWord(text: React.ReactNode): React.ReactNode {
  if (typeof text === 'string') {
    return brandParts(text, '');
  }
  if (Array.isArray(text)) {
    if (!text.some((chunk) => typeof chunk === 'string' && hasBrandWord(chunk))) {
      return text;
    }
    return text.flatMap((chunk, index) => {
      if (typeof chunk !== 'string' || !hasBrandWord(chunk)) {
        return [chunk];
      }
      const parts = brandParts(chunk, `${index}-`);
      return Array.isArray(parts) ? parts : [parts];
    });
  }
  return text;
}

/**
 * The `textComponent` of the app intl: every `<FormattedMessage>` renders its text through it,
 * so a message with "MUSIC24" draws the brand word with no change at the call site.
 */
export const BrandText: React.FC<React.PropsWithChildren> = ({ children }) => (
  <>{withBrandWord(React.Children.toArray(children))}</>
);
